import { Component, computed, inject, Input, OnChanges, signal } from '@angular/core';
import { MultiShipmentExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { PolarisChoice } from '@app/sharedModule/components/polaris/components/choice-list/s-polaris-choice-list.component';
import { ShopService } from '@services/shops/shop.service';
import {
  ModalItemView,
  modalItems,
  MULTI_SHIPMENT_PREVIEW,
  PREVIEW_SHIPPING_RATES,
  resolveLabelTokens,
} from '../../multi-shipment-example.model';

type StepKey = 'items' | 'address' | 'shipping' | 'giftNote';

interface ModalStep {
  key: StepKey;
  title: string;
  description: string;
}

@Component({
  selector: 'multi-shipment-recipient-modal-preview',
  templateUrl: './add-recipient-modal-preview.component.html',
  styleUrls: ['./add-recipient-modal-preview.component.scss'],
  standalone: false,
})
export class RecipientModalPreviewComponent implements OnChanges {
  @Input() modal: MultiShipmentExtension.AddRecipientModal;

  readonly currentStepIndex = signal(0);
  private readonly steps = signal<ModalStep[]>([]);
  readonly currentStep = computed(() => this.steps()[this.currentStepIndex()]);
  readonly progress = computed(() => ((this.currentStepIndex() + 1) / Math.max(this.steps().length, 1)) * 100);
  readonly isFirstStep = computed(() => this.currentStepIndex() === 0);
  readonly isLastStep = computed(() => this.currentStepIndex() === this.steps().length - 1);

  title = '';
  messageLimit = 0;
  readonly items: ModalItemView[] = modalItems();
  private readonly currencySymbol = inject(ShopService).shopCurrencySymbol || '$';
  readonly shippingChoices: PolarisChoice[] = PREVIEW_SHIPPING_RATES.map((rate, index) => ({
    title: rate.name,
    trailing: `${this.currencySymbol}${rate.price}`,
    selected: index === 0,
  }));

  ngOnChanges(): void {
    this.steps.set([
      { key: 'items', title: this.modal?.items?.title ?? '', description: this.modal?.items?.description ?? '' },
      { key: 'address', title: this.modal?.address?.title ?? '', description: this.modal?.address?.description ?? '' },
      {
        key: 'shipping',
        title: this.modal?.shipping?.title ?? '',
        description: this.modal?.shipping?.description ?? '',
      },
      {
        key: 'giftNote',
        title: this.modal?.giftNote?.title ?? '',
        description: this.modal?.giftNote?.description ?? '',
      },
    ]);
    this.title = resolveLabelTokens(this.modal?.addRecipientTitle ?? '', {
      number: MULTI_SHIPMENT_PREVIEW.modalRecipientNumber,
    });
    this.messageLimit = this.modal?.giftNote?.messageLimit ?? 0;
    if (this.currentStepIndex() > this.steps().length - 1) {
      this.currentStepIndex.set(0);
    }
  }

  next(): void {
    if (!this.isLastStep()) {
      this.currentStepIndex.update((index) => index + 1);
    }
  }

  back(): void {
    if (!this.isFirstStep()) {
      this.currentStepIndex.update((index) => index - 1);
    }
  }

  reset(): void {
    this.currentStepIndex.set(0);
  }
}
